"use client";

import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import DynamicForm from "./dynamic-form";
import { Page } from "../../types/page";
import { Question } from "../../types/question";
import { CalendarCheck, Check, Loader2, Plus } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { createDynamicSchema } from "../../helper/dynamicSchama";
import { Location, LocationType } from "../../types/location";
import {
  convertToMinutes,
  extractUTMParameters,
  formatPrice,
} from "../../helper/utility";
import { Price } from "../../types/resource";
import { Textarea } from "../../components/ui/textarea";
import { getLocationIcon } from "./location-icon";
import { SelectedSlot } from "../../types/slots";
import { DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { SlotTime } from "./slot-time";
import { ScrollArea } from "../../components/ui/scroll-area";

interface BookingFormProps {
  page: Page;
  onSubmit: (data: any) => void;
  onBack: () => void;
  resource: any;
  isSubmitting: boolean;
  queryParams?: Record<string, string>;
  slot?: SelectedSlot;
  isDialog?: boolean;
}

export function BookingForm({
  page,
  onSubmit,
  onBack,
  slot,
  resource,
  isSubmitting,
  queryParams = {},
  isDialog = false,
}: BookingFormProps) {
  const [formData, setFormData] = useState<any>({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [selectedLocation, setSelectedLocation] = useState(
    resource.locations[0]
  );

  const [questions, setQuestions] = useState<Question[]>(resource.questions);

  let prices = resource.prices;
  if (prices) {
    const filtered = prices.filter(
      (x: Price) =>
        !x.duration || convertToMinutes(x.duration) === slot?.duration
    );
    prices = filtered.length > 0 ? filtered : prices;
  }

  const [selectedPrice, setSelectedPrice] = useState(
    resource.payment?.is_enabled ? prices[0] : undefined
  );
  const [selectedGateway, setSelectedGateway] = useState(
    resource.payment?.is_enabled ? resource.payment?.gateways?.[0] : undefined
  );
  const [showGuestEmails, setShowGuestEmails] = useState(false);
  const [guestEmails, setGuestEmails] = useState("");
  const [locationDetails, setLocationDetails] = useState("");

  useEffect(() => {
    const updatedFormData = { ...formData };
    questions.forEach((q) => {
      if (queryParams[q.name]) {
        updatedFormData[q.name] = queryParams[q.name];
      }
    });
    setFormData(updatedFormData);
  }, []); // only run on initial mount

  useEffect(() => {
    const isInboundCall = selectedLocation?.type === "inbound_call";

    setQuestions((prevQuestions: Question[]) => {
      const index = prevQuestions.findIndex((q) =>
        ["tel", "phone", "inbound_call"].includes(q.type)
      );

      if (index === -1) {
        if (
          resource.locations.some((x: Location) => x.type === "inbound_call")
        ) {
          return [
            ...prevQuestions,
            {
              name: "inbound_call",
              label: "Phone number",
              type: "tel",
              value: "",
              required: isInboundCall,
            },
          ];
        }
      } else {
        const updated = [...prevQuestions];
        updated[index] = {
          ...updated[index],
          required: isInboundCall ? true : updated[index].required,
        };
        return updated;
      }

      return prevQuestions;
    });
  }, [selectedLocation, resource.locations]);

  const handleFormChange = (name: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: undefined }));
    }
  };

  const extractInvitee = () => {
    const inviteeData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      questions: questions
        .filter((q: Question) => !["name", "email", "phone"].includes(q.name))
        .map((q: Question) => ({
          name: q.name,
          label: q.label,
          type: q.type,
          value: formData[q.name],
        })),
      time_zone: slot?.time_zone,
      source_url: window.location.href,
      utm: extractUTMParameters(queryParams),
    };
    // Extract phone by type if not set already
    if (!inviteeData.phone) {
      const field = questions.find((x: Question) => x.type == "tel")?.name;
      if (field) {
        inviteeData.phone = formData[field];
      }
    }
    return inviteeData;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Create dynamic schema based on questions
    const schema = createDynamicSchema(questions);

    // Validate form data
    const validationResult = await schema.safeParseAsync(formData);

    if (!validationResult.success) {
      const formattedErrors: { [key: string]: string } = {};
      validationResult.error.issues.forEach((err) => {
        if (err.path.length > 0) {
          formattedErrors[err.path[0].toString()] = err.message;
        }
      });
      console.log("errors", formattedErrors);
      setErrors(formattedErrors);
      return;
    }

    // Validate location details if required
    const hasAskInvitee = resource.locations.some(
      (loc: Location) => loc.type === "ask_invitee"
    );
    const onlyAskInvitee = hasAskInvitee && resource.locations?.length === 1;
    if (
      hasAskInvitee &&
      selectedLocation &&
      selectedLocation.type === "ask_invitee" &&
      !locationDetails
    ) {
      setErrors((prev: any) => ({
        ...prev,
        locationDetails: onlyAskInvitee
          ? "Location details are required"
          : "Please provide location details",
      }));
      return;
    }

    const inviteeData = extractInvitee();

    // Add location details to the booking data if provided
    let location = selectedLocation;
    if (selectedLocation) {
      if (selectedLocation.type === "ask_invitee") {
        location = { ...selectedLocation, address: locationDetails };
      } else if (selectedLocation.type === "inbound_call") {
        location = { ...selectedLocation, phone: inviteeData.phone };
      }
    }

    onSubmit({
      invitees: [
        {
          ...inviteeData,
          payment: selectedGateway
            ? { ...selectedPrice, gateway: selectedGateway }
            : undefined,
          guests: guestEmails
            ?.replace(/\r?\n/g, ",")
            .split(",")
            .filter((x: string) => x != ""),
        },
      ],
      location: location,
    });
  };

  const getPriceKey = (price: Price) => {
    return `${price.amount}_${price.currency}`;
  };

  const getSubmitText = () => {
    if (selectedPrice) {
      return "Pay and Confirm";
    } else if (resource.settings?.require_approval) {
      return "Submit for Approval";
    } else {
      return "Confirm Booking";
    }
  };

  const formRenderer = () => {
    return (
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4 mb-6">
          {questions.map((question: Question) => (
            <DynamicForm
              key={question.name}
              domain={page.domain}
              question={question}
              value={formData[question.name]}
              onChange={(value) => handleFormChange(question.name, value)}
              error={errors[question.name]}
            />
          ))}

          {/* Location Selection */}
          {resource.locations?.length > 1 && (
            <div className="flex flex-col gap-2">
              <Label className="font-medium text-base">Select Location</Label>
              <RadioGroup
                value={selectedLocation.type}
                onValueChange={(value) => {
                  const location = resource.locations.find(
                    (l: Location) => l.type === value
                  );
                  setSelectedLocation(location);
                }}
                className="grid grid-cols-2 gap-4"
              >
                {resource.locations.map((location: Location) => (
                  <Label
                    key={location.type}
                    htmlFor={location.type}
                    className={`flex flex-col p-4 border rounded-lg cursor-pointer hover:border-primary transition-colors relative ${
                      selectedLocation.type === location.type
                        ? "border-primary bg-primary/5"
                        : ""
                    }`}
                  >
                    <RadioGroupItem
                      value={location.type}
                      id={location.type}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-2">
                      {getLocationIcon(location.type as LocationType)}
                      <div className="font-medium">{location.name}</div>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {location.remarks}
                    </div>
                    {selectedLocation.type === location.type && (
                      <div className="absolute top-2 right-2 text-primary">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Location Details for Ask Invitee */}
          {selectedLocation && selectedLocation.type === "ask_invitee" && (
            <div className="space-y-2">
              <Label htmlFor="location" className="font-medium text-base">
                Location
                {resource.locations.length === 1 && (
                  <span className="text-red-500">*</span>
                )}
              </Label>
              <Textarea
                id="location"
                value={locationDetails}
                onChange={(e) => {
                  setLocationDetails(e.target.value);
                  if (errors.locationDetails) {
                    setErrors((prev: any) => ({
                      ...prev,
                      locationDetails: undefined,
                    }));
                  }
                }}
                placeholder="Please provide the location details..."
                className={errors.locationDetails ? "border-red-500" : ""}
              />
              {errors.locationDetails && (
                <p className="text-sm text-red-500">{errors.locationDetails}</p>
              )}
            </div>
          )}

          {resource.payment?.is_enabled === true && (
            <>
              {/* Price Selection */}
              {prices.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Label className="font-medium text-base">Select Price</Label>
                  <RadioGroup
                    value={getPriceKey(selectedPrice)}
                    onValueChange={(value) => {
                      const [amount, currency] = value.split("_");
                      const price = prices.find(
                        (p: any) =>
                          p.amount.toString() === amount &&
                          p.currency === currency
                      );
                      setSelectedPrice(price);
                    }}
                    className="grid grid-cols-2 gap-4"
                  >
                    {prices.map((price: Price) => (
                      <Label
                        key={getPriceKey(price)}
                        htmlFor={getPriceKey(price)}
                        className={`flex flex-col p-4 border rounded-lg cursor-pointer hover:border-primary transition-colors relative ${
                          getPriceKey(selectedPrice) === getPriceKey(price)
                            ? "border-primary bg-primary/5"
                            : ""
                        }`}
                      >
                        <RadioGroupItem
                          value={getPriceKey(price)}
                          id={getPriceKey(price)}
                          className="sr-only"
                        />
                        <div className="font-medium">
                          {formatPrice(price.amount, price.currency)}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                          <span>{price.name}</span>
                        </div>
                        {getPriceKey(selectedPrice) === getPriceKey(price) && (
                          <div className="absolute top-2 right-2 text-primary">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
              )}

              {/* Payment Gateway Selection */}
              {resource.payment.gateways.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Label className="font-medium text-base">
                    Select Payment Method
                  </Label>
                  <RadioGroup
                    value={selectedGateway}
                    onValueChange={setSelectedGateway}
                    className="grid grid-cols-2 gap-4"
                  >
                    {resource.payment.gateways.map((gateway: string) => (
                      <Label
                        key={gateway}
                        htmlFor={gateway}
                        className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer hover:border-primary transition-colors relative ${
                          selectedGateway === gateway
                            ? "border-primary bg-primary/5"
                            : ""
                        }`}
                      >
                        <RadioGroupItem
                          value={gateway}
                          id={gateway}
                          className="sr-only"
                        />
                        <div className="w-8 h-8 relative flex-shrink-0">
                          <img
                            src={`https://cdn.dayschedule.com/icon/${gateway}.png`}
                            alt={gateway}
                            className="object-contain"
                          />
                        </div>
                        <div className="font-medium">
                          {gateway.charAt(0).toUpperCase() + gateway.slice(1)}
                        </div>
                        {selectedGateway === gateway && (
                          <div className="absolute top-2 right-2 text-primary">
                            <Check className="w-4 h-4" />
                          </div>
                        )}
                      </Label>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </>
          )}

          {/* Guest Emails Section */}
          {resource.settings?.guest?.is_allowed === true && (
            <div className="space-y-4">
              {!showGuestEmails ? (
                <Button
                  type="button"
                  variant="link"
                  className="flex items-center justify-start gap-2 px-0"
                  onClick={() => setShowGuestEmails(true)}
                >
                  <Plus className="w-4 h-4" />
                  Add Guests
                </Button>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="guests">Guest Emails</Label>
                  <Textarea
                    id="guests"
                    value={guestEmails}
                    onChange={(e) => setGuestEmails(e.target.value)}
                    placeholder="Enter email addresses..."
                    className="min-h-[100px]"
                  />
                  <p className="text-sm text-muted-foreground">
                    Maximum allowed {resource.settings.guest.max_allowed}. One
                    email per line, or comma separated.
                  </p>
                  {errors.guests && (
                    <p className="text-sm text-destructive">{errors.guests}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={
            isDialog
              ? "sticky bottom-0 flex py-4 mt-4 bg-background gap-4"
              : "flex flex-row gap-4"
          }
        >
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CalendarCheck className="mr-2 h-4 w-4" />
            )}
            {getSubmitText()}
          </Button>
          <Button type="button" variant={"outline"} onClick={onBack}>
            Cancel
          </Button>
        </div>
      </form>
    );
  };

  if (isDialog) {
    return (
      <>
        <DialogHeader className="p-4">
          <DialogTitle className="text-xl font-bold">
            {resource.form?.heading || "Complete Your Booking"}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-150px)]">
          <div className="px-6">
            <div className="mb-4">
              <SlotTime slot={slot}></SlotTime>
            </div>
            {formRenderer()}
          </div>
        </ScrollArea>
      </>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-xl mx-auto">
        <h2 className="text-xl font-bold mb-6">
          {resource.form?.heading || "Complete Your Booking"}
        </h2>
        <div className="mb-4">
          <SlotTime slot={slot}></SlotTime>
        </div>
        {formRenderer()}
      </div>
    </div>
  );
}
